import { z } from 'zod';

export const fonteSchema = z.string().min(1).max(200);

export const articleInputSchema = z.object({
  anno: z.number().int().min(1900).max(2100),
  mese: z.number().int().min(1).max(12),
  data: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato data: YYYY-MM-DD'),
  fonte: fonteSchema,
  titolo: z.string().min(1).max(2000),
  contenuto: z.string().min(1),
});

export type ArticleInput = z.infer<typeof articleInputSchema>;

export const articleSchema = articleInputSchema.extend({
  id: z.string().uuid(),
  contentHash: z.string().length(64),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string().nullable(),
});

export type Article = z.infer<typeof articleSchema>;

export const searchParamsSchema = z.object({
  q: z.string().optional(), // ricerca combinata legacy (admin, /cerca, bookmark)
  titolo: z.string().optional(), // ricerca solo nel titolo
  testo: z.string().optional(), // ricerca solo nel contenuto
  quaderni: z.string().optional(), // ricerca solo nei titoli-sezione dei Quaderni
  anno: z.coerce.number().int().min(1900).max(2100).optional(),
  mese: z.coerce.number().int().min(1).max(12).optional(),
  fonte: z.string().optional(),
  dataFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dataTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page: z.coerce.number().int().min(1).default(1),
  sort: z.enum(['date_desc', 'date_asc']).default('date_asc').catch('date_asc'),
});

export type SearchParams = z.infer<typeof searchParamsSchema>;

export const searchResultItemSchema = z.object({
  id: z.string().uuid(),
  data: z.string(),
  fonte: z.string(),
  titolo: z.string(),
  titoloHl: z.string(),
  snippet: z.string(),
  rank: z.number().nullable(),
});

export type SearchResultItem = z.infer<typeof searchResultItemSchema>;

export const searchResponseSchema = z.object({
  items: z.array(searchResultItemSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
  totalPages: z.number().int(),
  capped: z.boolean(),
});

export type SearchResponse = z.infer<typeof searchResponseSchema>;

// Indice per data: per ogni anno, conteggi articoli dei 12 mesi (indice 0 = gennaio).
export const dateIndexYearSchema = z.object({
  anno: z.number().int(),
  months: z.array(z.number().int()).length(12),
});

export const dateIndexSchema = z.array(dateIndexYearSchema);

export type DateIndex = z.infer<typeof dateIndexSchema>;

// Username: allineato al validator di default del plugin better-auth
// (alfanumerico + underscore, 3–30). La normalizzazione lowercase è lato server.
export const usernameSchema = z
  .string()
  .min(3, 'Minimo 3 caratteri')
  .max(30, 'Massimo 30 caratteri')
  .regex(/^[a-zA-Z0-9_]+$/, 'Solo lettere, numeri e underscore');

export const passwordSchema = z.string().min(1, 'Inserisci una password').max(256);

// Campi permessi riusabili. allowedFonti vuoto/assente = nessuna fonte
// (fail-closed per i lettori). dataFrom/dataTo null/assente = nessun limite.
const accessFields = {
  allowedFonti: z.array(fonteSchema).optional(),
  dataFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  dataTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
};

export const createUserSchema = z
  .object({
    username: usernameSchema,
    password: passwordSchema,
    role: z.enum(['admin', 'user']).default('user'),
    ...accessFields,
  })
  .refine((d) => d.role === 'admin' || (d.allowedFonti?.length ?? 0) > 0, {
    message: 'Seleziona almeno una fonte per un lettore',
    path: ['allowedFonti'],
  });

export type CreateUserInput = z.infer<typeof createUserSchema>;

export const userSchema = z.object({
  id: z.string(),
  username: z.string().nullable(),
  role: z.enum(['admin', 'user']),
  banned: z.boolean(),
  banReason: z.string().nullable(),
  banExpires: z.string().nullable(),
  allowedFonti: z.array(z.string()).nullable(),
  dataFrom: z.string().nullable(),
  dataTo: z.string().nullable(),
  createdAt: z.string(),
});

export type User = z.infer<typeof userSchema>;

export const updateUserSchema = z.object({
  role: z.enum(['admin', 'user']).optional(),
  banned: z.boolean().optional(),
  banReason: z.string().max(500).nullable().optional(),
  banExpires: z.string().nullable().optional(),
  newPassword: passwordSchema.optional(),
  ...accessFields,
});

export type UpdateUserInput = z.infer<typeof updateUserSchema>;
